import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import { IoArrowBack } from "react-icons/io5";
import { Skeleton } from "@mui/material";

const NewGRN = () => {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const poIdFromQuery = searchParams.get("poId") || "";

  const [selectedPO, setSelectedPO] = useState(poIdFromQuery);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [poDetails, setPoDetails] = useState(null);
  const [receivedDate, setReceivedDate] = useState(new Date().toISOString().split("T")[0]);
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]);

  const [loadingPOs, setLoadingPOs] = useState(true);
  const [loadingPO, setLoadingPO] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    fetchPurchaseOrders();
  }, []);

  useEffect(() => {
    if (selectedPO) {
      fetchPODetails(selectedPO);
    } else {
      setPoDetails(null);
      setItems([]);
    }
  }, [selectedPO]);

  const fetchPurchaseOrders = async () => {
    setLoadingPOs(true);
    try {
      const responses = await Promise.all([
        fetch("/api/getAllPurchaseOrders?status=ordered&limit=100", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch("/api/getAllPurchaseOrders?status=partially_received&limit=100", {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const results = await Promise.all(responses.map((r) => r.json()));
      const allPOs = [
        ...(results[0].purchaseOrders || []),
        ...(results[1].purchaseOrders || []),
      ];
      setPurchaseOrders(allPOs);
    } catch {
      setErrorMessage("Failed to load purchase orders");
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setLoadingPOs(false);
    }
  };

  const fetchPODetails = async (poId) => {
    setLoadingPO(true);
    try {
      const response = await fetch(`/api/getPurchaseOrder/${poId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok && data.purchaseOrder) {
        const po = data.purchaseOrder;
        setPoDetails(po);
        setItems(
          po.items.map((item) => ({
            item: item.item?._id || item.item,
            itemName: item.item?.name || "N/A",
            itemSku: item.item?.sku || "",
            orderedQty: item.qty || 0,
            previouslyReceived: item.receivedQty || 0,
            qtyToReceive: Math.max(0, (item.qty || 0) - (item.receivedQty || 0)),
            acceptedQty: Math.max(0, (item.qty || 0) - (item.receivedQty || 0)),
            rejectedQty: 0,
            rejectionReason: "",
          }))
        );
      } else {
        setErrorMessage(data.error || "Failed to load PO details");
        setTimeout(() => setErrorMessage(""), 3000);
      }
    } catch {
      setErrorMessage("Failed to load PO details");
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setLoadingPO(false);
    }
  };

  const updateItem = (index, field, value) => {
    const updated = [...items];
    const numValue = parseFloat(value) || 0;

    if (field === "qtyToReceive") {
      const remaining = updated[index].orderedQty - updated[index].previouslyReceived;
      const capped = Math.min(Math.max(0, numValue), remaining);
      updated[index].qtyToReceive = capped;
      updated[index].acceptedQty = Math.min(updated[index].acceptedQty, capped);
      updated[index].rejectedQty = capped - updated[index].acceptedQty;
    } else if (field === "acceptedQty") {
      const capped = Math.min(Math.max(0, numValue), updated[index].qtyToReceive);
      updated[index].acceptedQty = capped;
      updated[index].rejectedQty = updated[index].qtyToReceive - capped;
    } else if (field === "rejectionReason") {
      updated[index].rejectionReason = value;
    }

    setItems(updated);
  };

  const validateForm = () => {
    if (!selectedPO) {
      setErrorMessage("Please select a purchase order");
      return false;
    }
    if (items.every((i) => i.qtyToReceive === 0)) {
      setErrorMessage("At least one item must have a quantity to receive");
      return false;
    }
    const hasRejectedWithoutReason = items.some((i) => i.rejectedQty > 0 && !i.rejectionReason.trim());
    if (hasRejectedWithoutReason) {
      setErrorMessage("Please provide a rejection reason for all rejected items");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setTimeout(() => setErrorMessage(""), 3000);
      return;
    }

    setSaving(true);
    try {
      const response = await fetch("/api/addGRN", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          purchaseOrder: selectedPO,
          receivedDate,
          notes,
          items: items
            .filter((i) => i.qtyToReceive > 0)
            .map((i) => ({
              item: i.item,
              qtyReceived: i.qtyToReceive,
              acceptedQty: i.acceptedQty,
              rejectedQty: i.rejectedQty,
              rejectionReason: i.rejectionReason || undefined,
            })),
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setSuccessMessage("Goods received note created successfully");
        setTimeout(() => navigate("/GRN"), 1500);
      } else {
        setErrorMessage(data.error || "Failed to create GRN");
        setTimeout(() => setErrorMessage(""), 3000);
      }
    } catch {
      setErrorMessage("Failed to create GRN");
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate("/GRN")}
          className="p-2 rounded-full hover:bg-gray-100"
        >
          <IoArrowBack className="text-xl text-primary" />
        </button>
        <h2 className="text-xl font-semibold text-gray-800">New Goods Received Note</h2>
      </div>

      {/* Messages */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
          {successMessage}
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6">
        {/* PO Selection and Date */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Purchase Order <span className="text-red-500">*</span>
            </label>
            {loadingPOs ? (
              <Skeleton variant="rectangular" height={38} sx={{ borderRadius: 1 }} />
            ) : (
              <select
                value={selectedPO}
                onChange={(e) => setSelectedPO(e.target.value)}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Purchase Order</option>
                {purchaseOrders.map((po) => (
                  <option key={po._id} value={po._id}>
                    {po.poNumber} - {po.vendor?.name || "Unknown Vendor"}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block font-medium mb-2 text-blue-500">
              Received Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={receivedDate}
              onChange={(e) => setReceivedDate(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* PO Info */}
        {poDetails && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-500">PO Number</p>
                <p className="font-medium text-primary">{poDetails.poNumber}</p>
              </div>
              <div>
                <p className="text-gray-500">Vendor</p>
                <p className="font-medium">{poDetails.vendor?.name || "N/A"}</p>
              </div>
              <div>
                <p className="text-gray-500">Total Amount</p>
                <p className="font-medium">Nu. {(poDetails.totalAmount || 0).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-gray-500">Status</p>
                <p className="font-medium capitalize">{poDetails.status?.replace(/_/g, " ")}</p>
              </div>
            </div>
          </div>
        )}

        {/* Items Table */}
        {loadingPO ? (
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1, mb: 3 }} />
        ) : items.length > 0 ? (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Items to Receive
            </label>
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase">#</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase">Item</th>
                      <th className="text-center px-3 py-2 text-xs font-medium text-gray-500 uppercase">Ordered</th>
                      <th className="text-center px-3 py-2 text-xs font-medium text-gray-500 uppercase">Prev. Received</th>
                      <th className="text-center px-3 py-2 text-xs font-medium text-gray-500 uppercase">Qty to Receive</th>
                      <th className="text-center px-3 py-2 text-xs font-medium text-gray-500 uppercase">Accepted</th>
                      <th className="text-center px-3 py-2 text-xs font-medium text-gray-500 uppercase">Rejected</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase">Rejection Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => {
                      const remaining = item.orderedQty - item.previouslyReceived;
                      return (
                        <tr key={index} className="border-t">
                          <td className="px-3 py-2 text-sm text-gray-500">{index + 1}</td>
                          <td className="px-3 py-2 text-sm">
                            <div>
                              <p className="font-medium">{item.itemName}</p>
                              <p className="text-xs text-gray-400">{item.itemSku}</p>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-sm text-center">{item.orderedQty}</td>
                          <td className="px-3 py-2 text-sm text-center">
                            <span className={item.previouslyReceived > 0 ? "text-green-600 font-medium" : ""}>
                              {item.previouslyReceived}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <input
                              type="number"
                              min="0"
                              max={remaining}
                              value={item.qtyToReceive}
                              onChange={(e) => updateItem(index, "qtyToReceive", e.target.value)}
                              className="w-20 border border-gray-300 rounded px-2 py-1.5 text-sm text-center"
                            />
                          </td>
                          <td className="px-3 py-2 text-center">
                            <input
                              type="number"
                              min="0"
                              max={item.qtyToReceive}
                              value={item.acceptedQty}
                              onChange={(e) => updateItem(index, "acceptedQty", e.target.value)}
                              className="w-20 border border-gray-300 rounded px-2 py-1.5 text-sm text-center"
                            />
                          </td>
                          <td className="px-3 py-2 text-sm text-center">
                            <span className={item.rejectedQty > 0 ? "text-red-600 font-medium" : "text-gray-400"}>
                              {item.rejectedQty}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            {item.rejectedQty > 0 && (
                              <input
                                type="text"
                                placeholder="Reason for rejection"
                                value={item.rejectionReason}
                                onChange={(e) => updateItem(index, "rejectionReason", e.target.value)}
                                className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : selectedPO ? (
          <div className="text-center py-8 text-gray-400 mb-6">
            No items found for this purchase order
          </div>
        ) : null}

        {/* Notes */}
        <div className="mb-6">
          <label className="block font-medium mb-2 text-blue-500">
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Additional notes about this delivery"
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => navigate("/GRN")}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || items.length === 0}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Record Goods Receipt"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewGRN;
